import React, { useEffect, useLayoutEffect, useRef } from "react";
import ToggleSwitch from "./ToggleSwitch";

const FiltersPopover = ({
  isOpen,
  onClose,
  timeframeOnly = false,
  searchQuery = "",
  setSearchQuery = () => {},
  uploadTimeframe,
  setUploadTimeframe,
  uploadTimeframeOptions,
  filterMoreThanOneVideo = false,
  setFilterMoreThanOneVideo = () => {},
  showOtherOptions = false,
  setShowOtherOptions = () => {},
  onClearAll = () => {},
  activeFilterCount = 0,
  additionalFilters = [],
}) => {
  const popoverRef = useRef(null);

  useEffect(() => {
    if (!isOpen) return;
    const handleClickOutside = (e) => {
      if (popoverRef.current && !popoverRef.current.contains(e.target)) {
        onClose();
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen, onClose]);

  // Flip alignment if the popover overflows either edge of the viewport
  useLayoutEffect(() => {
    if (!isOpen || !popoverRef.current) return;
    const el = popoverRef.current;
    // Start right-aligned and measure
    el.style.right = "0";
    el.style.left = "auto";
    const rect = el.getBoundingClientRect();
    if (rect.left < 8) {
      // Overflows left — switch to left-aligned (opens rightward)
      el.style.right = "auto";
      el.style.left = "0";
    } else if (rect.right > window.innerWidth - 8) {
      // Overflows right — keep right-aligned (already set)
      el.style.right = "0";
      el.style.left = "auto";
    }
  }, [isOpen]);

  if (!isOpen) return null;

  if (timeframeOnly) {
    const handleTimeframeSelect = (value) => {
      setUploadTimeframe(value);
      onClose();
    };
    return (
      <div
        ref={popoverRef}
        className="absolute right-0 top-full mt-2 z-50 w-80 bg-theme-secondary border border-theme-secondary rounded-lg shadow-lg"
      >
        <div className="p-4">
          <span className="text-sm font-medium text-theme-text block mb-3">Upload window</span>
          <div className="grid grid-cols-5 gap-1 rounded-md border border-theme-secondary bg-theme-shadow p-1">
            {(uploadTimeframeOptions || []).map((option) => {
              const isActive = uploadTimeframe === option.value;
              return (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => handleTimeframeSelect(option.value)}
                  className={`px-1 py-1 text-xs rounded-md transition-colors ${
                    isActive
                      ? "bg-theme-primary text-theme-button font-medium"
                      : "text-theme-text opacity-80 hover:bg-theme-main"
                  }`}
                >
                  {option.label}
                </button>
              );
            })}
          </div>
        </div>
      </div>
    );
  }

  const handleTimeframeSelect = (value) => {
    setUploadTimeframe(value);
    onClose();
  };

  const handleToggleMoreThanOneVideo = (nextValue) => {
    setFilterMoreThanOneVideo(nextValue);
    onClose();
  };

  const handleToggleShowOtherOptions = (nextValue) => {
    setShowOtherOptions(nextValue);
    onClose();
  };

  return (
    <div
      ref={popoverRef}
      className="absolute right-0 top-full mt-2 z-50 w-80 bg-theme-secondary border border-theme-secondary rounded-lg shadow-lg"
    >
      <div className="p-4 space-y-4">
        <div className="flex items-center justify-between">
          <span className="text-sm font-medium text-theme-text">Filters</span>
          {activeFilterCount > 0 && (
            <button
              onClick={() => {
                onClearAll();
                onClose();
              }}
              className="text-xs text-theme-primary hover:underline"
            >
              Clear all
            </button>
          )}
        </div>

        <div>
          <label className="block text-xs text-theme-text opacity-60 mb-1.5">Search patterns</label>
          <div className="relative">
            <svg
              className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-theme-text opacity-50"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Filter by name..."
              className="w-full pl-9 pr-3 py-2 text-sm bg-theme-shadow border border-theme-secondary rounded-md text-theme-text placeholder:text-theme-text placeholder:opacity-50 focus:outline-none focus:ring-2 focus:ring-theme-primary focus:border-transparent"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery("")}
                className="absolute right-2.5 top-1/2 -translate-y-1/2 text-theme-text opacity-50 hover:opacity-100"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            )}
          </div>
        </div>

        <div>
          <label className="block text-xs text-theme-text opacity-60 mb-1.5">Uploaded</label>
          <div className="grid grid-cols-5 gap-1 rounded-md border border-theme-secondary bg-theme-shadow p-1">
            {(uploadTimeframeOptions || []).map((option) => {
              const isActive = uploadTimeframe === option.value;
              return (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => handleTimeframeSelect(option.value)}
                  className={`px-1 py-1 text-xs rounded-md transition-colors ${
                    isActive
                      ? "bg-theme-primary text-theme-button font-medium"
                      : "text-theme-text opacity-80 hover:bg-theme-main"
                  }`}
                >
                  {option.label}
                </button>
              );
            })}
          </div>
        </div>

        <div className="pt-2 border-t border-theme-secondary space-y-2">
          <ToggleSwitch
            checked={filterMoreThanOneVideo}
            onChange={handleToggleMoreThanOneVideo}
            label="> 1 video in pattern"
          />
          <ToggleSwitch
            checked={showOtherOptions}
            onChange={handleToggleShowOtherOptions}
            label="Include 'other' options"
          />
        </div>

        {additionalFilters.length > 0 && (
          <div className="pt-3 border-t border-theme-secondary">
            <span className="text-xs text-theme-text opacity-60 mb-2 block">Additional filters</span>
            <div className="flex flex-wrap gap-2">
              {additionalFilters.map((f) => (
                <span
                  key={f.key}
                  className="inline-flex items-center gap-1 pl-2.5 pr-1 py-1 rounded-full border border-theme-secondary bg-theme-main text-xs text-theme-text max-w-full"
                  title={f.label}
                >
                  <span className="truncate min-w-0">{f.label}</span>
                  <button
                    type="button"
                    onClick={() => {
                      f.onRemove();
                      onClose();
                    }}
                    className="p-0.5 rounded hover:bg-theme-secondary flex-shrink-0 text-theme-text opacity-70 hover:opacity-100"
                    aria-label={`Remove ${f.label}`}
                  >
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </span>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default FiltersPopover;
